/**
 * Sentry error monitoring (mobile + web).
 *
 * DESIGN:
 *  - DSN-GUARDED: no DSN → every export is a no-op. Toggling Sentry is a build-time env change.
 *  - PLATFORM-SPLIT DSN: native crashes go to the `beam-mobile` Sentry project, the getabeam.com web
 *    export goes to `beam-web`. Both DSNs are public-safe EXPO_PUBLIC_* values baked at export time.
 *  - ERRORS-ONLY: tracesSampleRate=0, no profiling (cost/noise posture, 2026-06-26).
 *  - PII-SAFE (mobile CLAUDE.md inv. 2 / no-PII-in-URLs): sendDefaultPii=false; beforeSend strips
 *    request bodies/cookies and keeps only a non-sensitive user id. No tokens, no customer data.
 */
import * as Sentry from '@sentry/react-native';
import { Platform } from 'react-native';

const DSN =
  Platform.OS === 'web' ? process.env.EXPO_PUBLIC_SENTRY_DSN_WEB : process.env.EXPO_PUBLIC_SENTRY_DSN;

let enabled = false;

/** Initialize Sentry once, at module load (before the app renders). No-op without a DSN. */
export function initSentry(): void {
  if (!DSN || enabled) return;
  Sentry.init({
    dsn: DSN,
    environment: process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT ?? (__DEV__ ? 'development' : 'production'),
    sendDefaultPii: false,
    tracesSampleRate: 0, // errors-only
    beforeSend(event) {
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        if (typeof event.request.url === 'string') {
          event.request.url = event.request.url.split('?')[0] ?? event.request.url;
        }
      }
      if (event.user) {
        if (event.user.id) event.user = { id: event.user.id };
        else delete event.user;
      }
      return event;
    },
  });
  enabled = true;
}

export function isSentryEnabled(): boolean {
  return enabled;
}

/** Re-export wrap so the root layout can wrap the app (adds the React error boundary). */
export const wrap = Sentry.wrap;
